// Deterministic project-reference parsing and precedence shared by File
// Explorer presentation, queue hydration, and intake persistence.

export interface ProjectQuoteReferenceResult {
    quoteRef: string;
    rawFolderQuoteRef: string;
    source: 'user-confirmed' | 'folder' | 'sync-manifest' | 'folder-history' | 'unverified-structured' | 'none' | string;
}

export interface ProjectQuoteReferenceInput {
    id?: string | null;
    name?: string | null;
    title?: string | null;
    quoteRef?: string | null;
    quoteNumber?: string | null;
    folderName?: string | null;
    syncRelativePath?: string | null;
    relativePath?: string | null;
    path?: string | null;
    rawFolderQuoteRef?: string | null;
    sourceFolderQuoteRef?: string | null;
    userConfirmedQuoteRef?: boolean | string | null;
    confirmedQuoteRef?: string | null;
    quoteRefConfirmed?: boolean | null;
    projectFolder?: {
        sourcePath?: string | null;
        relativePath?: string | null;
        [key: string]: unknown;
    } | null;
    meta?: {
        isSynced?: boolean | null;
        quoteRef?: string | null;
        userConfirmedQuoteRef?: boolean | string | null;
        confirmedQuoteRef?: string | null;
        quoteRefConfirmed?: boolean | null;
        [key: string]: unknown;
    } | null;
    formData?: {
        orderNumber?: string | null;
        userConfirmedOrderNumber?: string | null;
        confirmedOrderNumber?: string | null;
        orderNumberConfirmed?: boolean | null;
        [key: string]: unknown;
    } | null;
    [key: string]: unknown;
}

// RJ Bird folder references use a bounded base of four to eight digits and may
// carry either a one-digit dashed revision (`QR26944-1`) or a one/two-letter
// suffix (`QR839029PR`). The right boundary is deliberately strict: a longer
// alphanumeric run or multi-digit dashed continuation invalidates the whole
// candidate instead of allowing the regex to return a shortened prefix.
const QUOTE_REFERENCE_RE = /(?:^|[^A-Z0-9])QR\s*[-_]?\s*(\d{4,8}(?:-\d|[A-Z]{1,2})?)(?=$|[^A-Z0-9_-]|[-_](?!\s*\d))/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function extractQuoteReference(...values: unknown[]): string {
    for (const value of values) {
        if (value === null || value === undefined) continue;
        const source = String(value);
        // Testing each value independently prevents ('QR', '959011') from
        // becoming a reference that did not exist in either source field.
        const match = source.match(QUOTE_REFERENCE_RE);
        if (match) return `QR${match[1]!.toUpperCase()}`;
    }
    return '';
}

function cleanString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function firstNonBlank(...values: unknown[]): string {
    for (const value of values) {
        const cleaned = cleanString(value);
        if (cleaned) return cleaned;
    }
    return '';
}

function leafName(value: unknown): string {
    const normalised = cleanString(value).replace(/\\/g, '/').replace(/\/+$/, '');
    return normalised ? normalised.slice(normalised.lastIndexOf('/') + 1).trim() : '';
}

/**
 * Normalise a standalone quote-request value without accepting prose-shaped
 * lookalikes. Non-QR order references are retained verbatim as a legacy
 * fallback, but QR-shaped values always use one canonical representation.
 */
export function normaliseQuoteReference(value: unknown): string {
    const cleaned = cleanString(value);
    if (!cleaned) return '';
    const extracted = extractQuoteReference(cleaned);
    if (extracted) return extracted;
    const bare = cleaned.match(/^(\d{4,8}(?:-\d|[A-Z]{1,2})?)$/i);
    return bare ? `QR${bare[1]!.toUpperCase()}` : cleaned;
}

/**
 * `PR` is an RJ Bird folder-workflow suffix, not part of the client quote
 * request number. Keep the raw token separately for provenance while using
 * the client reference for quote identity.
 */
export function canonicaliseFolderQuoteReference(value: unknown): string {
    const normalised = normaliseQuoteReference(value);
    const workflowSuffix = normalised.match(/^QR(\d{4,8})PR$/i);
    return workflowSuffix ? `QR${workflowSuffix[1]}` : normalised;
}

function currentFolderQuoteReference(project: ProjectQuoteReferenceInput | Record<string, unknown> | null | undefined): string {
    const current = extractQuoteReference(
        project?.folderName,
        leafName((project as ProjectQuoteReferenceInput | null | undefined)?.projectFolder?.sourcePath),
        leafName((project as ProjectQuoteReferenceInput | null | undefined)?.projectFolder?.relativePath),
        leafName(project?.syncRelativePath),
        leafName(project?.relativePath),
        leafName(project?.path),
    );
    if (current) return current;
    // A durable UUID row's name/title is presentation history, not proof of
    // its current source folder. Old rows may retain the very stale label this
    // resolver is correcting, so prefer their structured identity instead.
    if (UUID_RE.test(cleanString(project?.id))) return '';
    return extractQuoteReference(project?.name, project?.title);
}

function historicalFolderQuoteReference(project: ProjectQuoteReferenceInput | Record<string, unknown> | null | undefined): string {
    return extractQuoteReference(
        project?.rawFolderQuoteRef,
        project?.sourceFolderQuoteRef,
    );
}

function explicitlyConfirmedQuoteReference(project: ProjectQuoteReferenceInput | Record<string, unknown> | null | undefined, meta: Record<string, unknown>, formData: Record<string, unknown>): string {
    const named = firstNonBlank(
        // Stage 1 is the current editable identity. Its explicit correction
        // must outrank confirmation copied into older import metadata.
        formData.userConfirmedOrderNumber,
        formData.confirmedOrderNumber,
        project?.userConfirmedQuoteRef,
        project?.confirmedQuoteRef,
        meta.userConfirmedQuoteRef,
        meta.confirmedQuoteRef,
    );
    if (named) return canonicaliseFolderQuoteReference(named);

    const confirmed = project?.quoteRefConfirmed === true
        || project?.userConfirmedQuoteRef === true
        || meta.quoteRefConfirmed === true
        || meta.userConfirmedQuoteRef === true
        || formData.orderNumberConfirmed === true;
    if (!confirmed) return '';
    return canonicaliseFolderQuoteReference(firstNonBlank(
        formData.orderNumber,
        project?.quoteRef,
        project?.quoteNumber,
        meta.quoteRef,
    ));
}

/**
 * Resolve canonical project identity and its provenance.
 *
 * Precedence is evidence-based:
 *   1. an explicit operator confirmation;
 *   2. the current source folder/path token;
 *   3. a quote ref read from a valid SyncManifest;
 *   4. historical folder and unverified structured/scanner values.
 *
 * This makes the current Gladesville/Pendle source folder defeat stale
 * scanner, raw-folder, or manifest values while still allowing a deliberate
 * operator correction to survive a local folder rename.
 */
export function resolveProjectQuoteReference(project: ProjectQuoteReferenceInput | Record<string, unknown> = {}): ProjectQuoteReferenceResult {
    const meta = project?.meta && typeof project.meta === 'object' ? project.meta as Record<string, unknown> : {};
    const formData = project?.formData && typeof project.formData === 'object'
        ? project.formData as Record<string, unknown>
        : {};
    const currentRawFolderQuoteRef = currentFolderQuoteReference(project);
    const historicalRawFolderQuoteRef = historicalFolderQuoteReference(project);
    const manifestRawQuoteRef = meta.isSynced === true
        ? extractQuoteReference(meta.quoteRef)
        : '';
    const structuredRawQuoteRef = extractQuoteReference(
        formData.orderNumber,
        project?.quoteRef,
        project?.quoteNumber,
    );
    // Keep the original PR-bearing token for audit/provenance even when the
    // only surviving source is a synced/restored metadata row.
    const rawFolderQuoteRef = currentRawFolderQuoteRef
        || historicalRawFolderQuoteRef
        || manifestRawQuoteRef
        || structuredRawQuoteRef;
    const currentFolderQuoteRef = canonicaliseFolderQuoteReference(currentRawFolderQuoteRef);
    const historicalFolderQuoteRef = canonicaliseFolderQuoteReference(historicalRawFolderQuoteRef);
    const confirmedQuoteRef = explicitlyConfirmedQuoteReference(project, meta, formData);
    const manifestQuoteRef = meta.isSynced === true
        ? canonicaliseFolderQuoteReference(meta.quoteRef)
        : '';
    const explicitQuoteRef = canonicaliseFolderQuoteReference(firstNonBlank(
        formData.orderNumber,
        project?.quoteRef,
        project?.quoteNumber,
    ));

    // An intentionally cleared edit is distinct from missing extraction.
    // Retain source provenance, but do not silently resurrect an old number.
    if (formData.orderNumberConfirmed === true
        && typeof formData.userConfirmedOrderNumber === 'string'
        && cleanString(formData.userConfirmedOrderNumber) === '') {
        return { quoteRef: '', rawFolderQuoteRef, source: 'user-confirmed' };
    }

    if (confirmedQuoteRef) {
        return { quoteRef: confirmedQuoteRef, rawFolderQuoteRef, source: 'user-confirmed' };
    }
    if (currentFolderQuoteRef) {
        return { quoteRef: currentFolderQuoteRef, rawFolderQuoteRef, source: 'folder' };
    }
    if (manifestQuoteRef) {
        return { quoteRef: manifestQuoteRef, rawFolderQuoteRef, source: 'sync-manifest' };
    }
    if (historicalFolderQuoteRef) {
        return { quoteRef: historicalFolderQuoteRef, rawFolderQuoteRef, source: 'folder-history' };
    }
    return {
        quoteRef: explicitQuoteRef,
        rawFolderQuoteRef,
        source: explicitQuoteRef ? 'unverified-structured' : 'none',
    };
}

/** Automatic document extraction is fill-empty-only for project identity. */
export function shouldApplyAutomaticQuoteReference(existingValue: unknown): boolean {
    return cleanString(existingValue) === '';
}
